import React, { useEffect, useState } from 'react';
import { Joyride, STATUS } from 'react-joyride';

const TOUR_STORAGE_KEY = 'scholar.universal_tour.completed.v1';

const STEPS = [
  {
    target: '[data-testid="xavier-header-logo"]',
    title: 'Welcome to HumanCodex',
    content:
      'I am Xavier — Unified Knowledge Professor. This is a quick guided tour of the Gateway. It takes about 60 seconds. Two wings: University (courses) and Library (archives).',
    disableBeacon: true,
    placement: 'bottom',
  },
  {
    target: '[data-testid="nav-courses-link"]',
    title: 'Courses',
    content:
      '46 AI teaching minds across Art, Science, History, Literature, AI/ML, and Job Training. Click any course to meet its dedicated AI tutor.',
    placement: 'bottom',
  },
  {
    target: '[data-testid="free-only-filter"]',
    title: '16 Free Foundation Courses',
    content:
      'No-barriers learning. Toggle this filter to see only the courses that cost nothing — start here if it is your first visit.',
    placement: 'top',
  },
  {
    target: '[data-testid="consultation-3-button"]',
    title: 'Talk to the Architect for $3',
    content:
      'A 30-minute consultation with Roger Keyserling, Architect of the NextXus Federation. One flat fee, no upsell.',
    placement: 'bottom',
  },
  {
    target: '[data-testid="xavier-chatbot-trigger"]',
    title: 'Chat with Xavier',
    content:
      'Ask anything — courses, concepts, the Federation, the 112 Sacred Directives (Code Book Vol 1), or anything in the Library. I run on Gemini 2.5 Flash and obey the Truth Gate (DIR-000): I will tell you when I do not know.',
    placement: 'left',
  },
  {
    target: '[data-testid="nav-full-nextxus"]',
    title: 'Full NextXus Grid',
    content:
      'Step out to the Federation command center — Core, Truth Verification, Aria, Roger, Axiom, Oracle, MVS/Lumen, and Xavier. The Gateway is one node of many.',
    placement: 'bottom',
  },
];

/**
 * Universal Guided Tour
 * - Runs automatically on first visit.
 * - Can be re-launched any time via the floating "Take the tour" button
 *   or by dispatching the `scholar:start-tour` custom event.
 * - Stores completion in localStorage so it doesn't pester returning users.
 */
export default function UniversalTour() {
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    // Auto-start on first visit, after the page has settled
    const completed = typeof window !== 'undefined' && window.localStorage.getItem(TOUR_STORAGE_KEY);
    if (!completed) {
      const t = setTimeout(() => setRun(true), 1800);
      return () => clearTimeout(t);
    }
    return undefined;
  }, []);

  useEffect(() => {
    const onStart = () => {
      setStepIndex(0);
      setRun(true);
    };
    window.addEventListener('scholar:start-tour', onStart);
    return () => window.removeEventListener('scholar:start-tour', onStart);
  }, []);

  const handleCallback = (data) => {
    const { status, index, type } = data;
    if (type === 'step:after' || type === 'error:target_not_found') {
      setStepIndex(index + 1);
    }
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setRun(false);
      setStepIndex(0);
      try {
        window.localStorage.setItem(TOUR_STORAGE_KEY, new Date().toISOString());
      } catch (e) {
        /* localStorage may be unavailable in private mode — ignore */
      }
    }
  };

  return (
    <>
      <Joyride
        steps={STEPS}
        run={run}
        stepIndex={stepIndex}
        continuous
        showProgress
        showSkipButton
        scrollToFirstStep
        scrollOffset={120}
        disableScrolling={false}
        callback={handleCallback}
        locale={{
          back: 'Back',
          close: 'Close',
          last: 'Begin learning',
          next: 'Next',
          skip: 'Skip tour',
        }}
        styles={{
          options: {
            primaryColor: '#fbbf24', // amber-400
            backgroundColor: '#0f0f17',
            textColor: '#e5e7eb',
            arrowColor: '#0f0f17',
            overlayColor: 'rgba(0, 0, 0, 0.78)',
            zIndex: 10000,
            width: 380,
          },
          tooltipTitle: { color: '#fbbf24', fontWeight: 700, letterSpacing: '0.02em' },
          tooltipContent: { color: '#d1d5db', lineHeight: 1.5 },
          buttonNext: { borderRadius: 9999, padding: '8px 16px', fontWeight: 600, color: '#000' },
          buttonBack: { color: '#9ca3af', marginRight: 8 },
          buttonSkip: { color: '#6b7280' },
        }}
      />

      {/* Persistent re-launch button — bottom-left so it doesn't collide with the chatbot trigger.
          Pushed above the 252px cockpit footer per Architect Directive 2026-05-19. */}
      <button
        type="button"
        data-testid="restart-tour-button"
        onClick={() => window.dispatchEvent(new CustomEvent('scholar:start-tour'))}
        className="fixed left-6 z-[95] inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-900/90 hover:bg-gray-800 border border-amber-500/40 text-amber-300 text-xs font-semibold tracking-wide shadow-lg backdrop-blur-sm transition-colors"
        style={{ bottom: 'calc(252px + 1.5rem)' }}
        title="Replay the guided tour"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 12a9 9 0 1 1-3-6.7" />
          <path d="M21 4v5h-5" />
        </svg>
        Take the tour
      </button>
    </>
  );
}
